import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TabNav, ListItem, Button, Card, Spinner } from '../../../components/common';
import PasswordChangeModal from '../../../components/service/PasswordChangeModal';
import PasswordChangeCompleteModal from '../../../components/service/PasswordChangeCompleteModal';
import AccountDeleteModal from '../../../components/service/AccountDeleteModal';
import AccountDeleteCompleteModal from '../../../components/service/AccountDeleteCompleteModal';
import { userService, waitlistService } from '../../../api/services';
import { useAuth } from '../../../contexts/AuthContext';
import { myPageTabs } from '../../../constants/uiConstants';
import { useCountryOptions } from '../../../constants/countries';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { formatKstDateTime } from '../../../utils/dateUtils';
import { translateApiError } from '../../../utils/apiError';

export default function MyAccount() {
  const { t } = useTranslation();
  const countryOptions = useCountryOptions();
  const GENDER_LABELS = { male: t('mypage.gender_male'), female: t('mypage.gender_female') };
  const NOT_SET = <span className="text-mono-400">{t('mypage.not_set')}</span>;
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPasswordComplete, setShowPasswordComplete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteComplete, setShowDeleteComplete] = useState(false);
  const [waitlistItems, setWaitlistItems] = useState([]);

  // #110: 비로그인 시 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (!localStorage.getItem('accessToken')) {
      navigate('/login', { replace: true });
      return;
    }
    userService.getProfile()
      .then(setAccount)
      .catch((err) => setError(translateApiError(err, t, 'errors.generic')))
      .finally(() => setLoading(false));
    // 대기 등록 내역 로드
    userService.getMyWaitlist?.()
      .then((data) => setWaitlistItems(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => {});
  }, [navigate]);

  const handleTabChange = (tabId) => {
    if (tabId === 'reviews') {
      navigate('/mypage/reviews');
    } else if (tabId === 'reservation') {
      // 예약 내역은 별도 라우트(/reservation)지만 마이페이지 탭에서 접근
      navigate('/reservation');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) return <div className="bg-mono-50 min-h-screen flex items-center justify-center"><Spinner size="lg" /></div>;
  if (error) return <div className="bg-mono-50 min-h-screen"><p className="text-sm text-red-500 text-center py-16">{error}</p></div>;
  if (!account) return null;

  return (
    <div className="bg-mono-50 min-h-screen">
      {/* TabNav - fullwidth */}
      <TabNav tabs={myPageTabs} activeTab="account" onTabChange={handleTabChange} />

      <div className="max-w-content mx-auto px-2.5 sm:px-5 py-8 sm:py-12">

        {/* Section Heading + Action Buttons */}
        <div className="flex items-center justify-between mt-8 mb-4">
          <h2 className="text-xl font-bold text-mono-900">{t('mypage.account_info')}</h2>
          <div className="flex items-center gap-3">
            <Button level="level3" size="sm" onClick={() => navigate('/mypage/account/edit')}>{t('mypage.edit_account')}</Button>
            <Button level="level3" size="sm" onClick={handleLogout}>{t('common.logout')}</Button>
          </div>
        </div>

        {/* User Info Card */}
        <Card variant="padded">
          {/* Name & Email */}
          <div className="mb-4 pb-4 border-b border-mono-100">
            <h3 className="text-lg font-bold text-mono-900">{account.name}</h3>
            <p className="text-sm text-mono-500 mt-1">{account.email}</p>
          </div>

          {/* Info Rows */}
          <div className="space-y-1">
            <ListItem
              label={t('mypage.phone')}
              value={account.phone ? (formatPhoneNumberIntl(account.phone) || account.phone) : '-'}
              layout="inline"
            />
            <ListItem label={t('mypage.join_date')} value={formatKstDateTime(account.created_at) || '-'} layout="inline" />
            {/* BugHerd: 가입방식 표기 — DB 의 social_provider raw 값(kakao/naver/google) 을 그대로
                노출하면 다국어/UX 모두 어색. provider 별 표시명 i18n 키로 매핑. fallback 은 일반(이메일). */}
            <ListItem
              label={t('mypage.join_type')}
              value={account.social_provider
                ? t(`mypage.provider_${account.social_provider}`, { defaultValue: account.social_provider })
                : t('mypage.general')}
              layout="inline"
            />
            <ListItem
              label={t('mypage.country')}
              value={countryOptions.find((o) => o.value === account.country)?.label || NOT_SET}
              layout="inline"
            />
            <ListItem
              label={t('mypage.gender')}
              value={GENDER_LABELS[account.gender] || NOT_SET}
              layout="inline"
            />
          </div>
        </Card>

        {/* Password Change Card */}
        <Card variant="padded" className="mt-3">
          <div className="flex items-center gap-4 sm:gap-6">
            <span className="text-2sm font-medium text-mono-500 w-[80px] sm:w-[120px] shrink-0">{t('mypage.change_password')}</span>
            <Button level="level3" size="sm" onClick={() => setShowPasswordModal(true)}>{t('mypage.change_password')}</Button>
          </div>
        </Card>

        {/* Marketing Card */}
        <Card variant="padded" className="mt-3">
          <div className="flex items-center gap-4 sm:gap-6">
            <span className="text-2sm font-medium text-mono-500 w-[80px] sm:w-[120px] shrink-0">{t('mypage.marketing_consent')}</span>
            <span className="text-2sm font-medium text-mono-900">
              {account.marketing_consent ? t('mypage.marketing_allowed') : t('mypage.marketing_denied')}
            </span>
          </div>
        </Card>

        {/* 대기 등록 내역 */}
        {waitlistItems.length > 0 && (
          <Card variant="padded" className="mt-3">
            <h3 className="text-sm font-bold text-mono-900 mb-3">{t('mypage.waitlist_history')}</h3>
            <div className="space-y-2">
              {waitlistItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between py-2 border-b border-mono-100 last:border-0">
                  <div>
                    <span className="text-2sm font-medium text-mono-900">
                      {item.service_type === 'airport' ? t('service.airport') : item.service_type === 'golf' ? t('service.golf') : item.service_type === 'ski' ? t('service.ski') : item.service_type === 'hospital' ? t('service.hospital') : item.service_type === 'pet' ? t('service.pet') : item.service_type === 'business' ? t('service.business') : item.service_type === 'tour' ? t('service.tour') : item.service_type === 'shopping' ? t('service.shopping') : item.service_type}
                    </span>
                    <span className="text-2sm text-mono-500 ml-2">
                      {item.desired_date} {item.desired_time}
                    </span>
                  </div>
                  <span className={`text-2xs font-bold px-2 py-0.5 rounded-full ${item.status === 'waiting' ? 'bg-orange-50 text-orange-500' : item.status === 'notified' ? 'bg-blue-50 text-blue-500' : 'bg-mono-100 text-mono-500'}`}>
                    {item.status === 'waiting' ? t('reservation.waiting_status') : item.status === 'notified' ? t('reservation.waiting_notified') : item.status === 'converted' ? t('reservation.waiting_converted') : item.status === 'cancelled' ? t('reservation.waiting_cancelled') : item.status}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Account Deletion */}
        <div className="flex justify-end mt-6">
          <Button level="level3" size="sm" className="text-mono-400" onClick={() => setShowDeleteModal(true)}>{t('mypage.delete_account')}</Button>
        </div>
      </div>

      {/* Modals */}
      {showPasswordModal && (
        <PasswordChangeModal
          onClose={() => setShowPasswordModal(false)}
          onComplete={() => {
            setShowPasswordModal(false);
            setShowPasswordComplete(true);
          }}
        />
      )}
      {showPasswordComplete && (
        <PasswordChangeCompleteModal
          onClose={() => setShowPasswordComplete(false)}
        />
      )}
      {showDeleteModal && (
        <AccountDeleteModal
          onClose={() => setShowDeleteModal(false)}
          onComplete={() => {
            // AccountDeleteModal 이 deleteAccount 직후 clearAuth() 로 로컬 세션을
            // 이미 정리. 여기서 logout() 추가 호출 시 BE 401 → interceptor redirect 가
            // 발생해 잔존 토큰으로 자동 로그인되는 회귀 발생 → 호출 제거.
            setShowDeleteModal(false);
            setShowDeleteComplete(true);
          }}
        />
      )}
      {showDeleteComplete && (
        <AccountDeleteCompleteModal
          onClose={() => {
            // 탈퇴 완료 후 마이페이지 잔존 state 정리 차원으로 메인으로 강제 이동.
            // 단순 setState(false) 만 두면 token 없는 마이페이지 화면이 잠시 노출됨.
            setShowDeleteComplete(false);
            navigate('/', { replace: true });
          }}
        />
      )}
    </div>
  );
}
